import React, { useState, useEffect, useCallback, useRef } from 'react'
import _ from 'lodash'
import t from '~t'
import { useDispatch, useSelector } from 'react-redux'
import { user as getUser, userStatus, errorReason } from '~data/selectors/user'
import { save } from '~data/actions/user'

import { Error, Alert } from '~co/overlay/dialog'

/*
    Component, [ 'name', ...etc ]
        ->
            unsaved: Bool
            status: 'loading', ...etc
            onChange, for input
            onSubmit
*/
export default function withEdit(Component, fields = []) {
    function WithEditWrap(props) {
        const dispatch = useDispatch()
        const user = useSelector(state => getUser(state))
        const status = useSelector(state => userStatus(state).save)
        const saveBound = useCallback((...args) => dispatch(save(...args)), [dispatch])

        const [state, setState] = useState({ unsaved: false, user })

        const latest = useRef({ user, state })
        latest.current = { user, state }

        //reset local copy when the store user changes
        const prevUser = useRef(user)
        useEffect(() => {
            if (prevUser.current != user)
                setState({ unsaved: false, user })
            prevUser.current = user
        }, [user])

        const onChange = useCallback(e => {
            const { name, value } = e.currentTarget
            setState(prev => ({
                user: {
                    ...prev.user,
                    [name]: value
                },
                unsaved: true
            }))
        }, [])

        const onSubmit = useCallback(
            onSuccess => {
                const { user, state } = latest.current
                const emailIsChanged = state.user.email != user.email

                dispatch(
                    save(
                        _.pick(state.user, fields),
                        () => {
                            Alert(
                                t.s('saveSuccess') +
                                    (emailIsChanged ? `\n${t.s('confirmYourEmail')}` : '')
                            )

                            if (typeof onSuccess == 'function')
                                onSuccess()
                        },
                        Error
                    )
                )
            },
            [dispatch, fields]
        )

        return (
            <Component
                {...props}
                {...state.user}
                onChange={onChange}
                onSubmit={onSubmit}
                unsaved={state.unsaved}
                status={status}
                save={saveBound} />
        )
    }

    return WithEditWrap
}
